import React, { useState, useEffect, useCallback, useRef } from "react";

// Helper function to check if the URL is a video
const isVideo = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

// Helper to calculate distance between two touch points
const getDistance = (touches) => {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

// Zoom bounds
const ZOOM_MIN = 1;
const ZOOM_MAX = 5;

// Helper to calculate the true rendered size of the media within the container (accounting for objectFit: "contain")
const getRenderedSize = (mediaElement) => {
  if (!mediaElement) return { renderedWidth: 0, renderedHeight: 0 };

  let naturalWidth, naturalHeight;

  // Handle both video and image elements
  if (mediaElement.tagName.toLowerCase() === "video") {
    naturalWidth = mediaElement.videoWidth;
    naturalHeight = mediaElement.videoHeight;
  } else {
    naturalWidth = mediaElement.naturalWidth;
    naturalHeight = mediaElement.naturalHeight;
  }

  const offsetWidth = mediaElement.offsetWidth;
  const offsetHeight = mediaElement.offsetHeight;

  if (!naturalWidth || !naturalHeight)
    return { renderedWidth: offsetWidth, renderedHeight: offsetHeight };

  const imgRatio = naturalWidth / naturalHeight;
  const containerRatio = offsetWidth / offsetHeight;
  let renderedWidth, renderedHeight;

  if (imgRatio > containerRatio) {
    renderedWidth = offsetWidth;
    renderedHeight = offsetWidth / imgRatio;
  } else {
    renderedHeight = offsetHeight;
    renderedWidth = offsetHeight * imgRatio;
  }
  return { renderedWidth, renderedHeight };
};

// Helper to calculate bounded offsets to prevent panning beyond the canvas/viewport borders
const calculateBoundedOffset = (
  targetX,
  targetY,
  currentScale,
  mediaElement,
  containerElement,
) => {
  if (!mediaElement || !containerElement || currentScale <= 1)
    return { x: 0, y: 0 };

  const { renderedWidth, renderedHeight } = getRenderedSize(mediaElement);
  const viewportWidth = containerElement.offsetWidth;
  const viewportHeight = containerElement.offsetHeight;

  const scaledWidth = renderedWidth * currentScale;
  const scaledHeight = renderedHeight * currentScale;

  let maxX = 0;
  let maxY = 0;

  // Calculate the max allowed offset if the scaled media is larger than the viewport
  if (scaledWidth > viewportWidth) {
    maxX = (scaledWidth - viewportWidth) / 2;
  }
  if (scaledHeight > viewportHeight) {
    maxY = (scaledHeight - viewportHeight) / 2;
  }

  // Clamp the target offsets
  return {
    x: Math.min(Math.max(targetX, -maxX), maxX),
    y: Math.min(Math.max(targetY, -maxY), maxY),
  };
};

const IMAGE_FULL = ({ isOpen, mediaList = [], initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Use refs to access latest state inside native event listeners without re-binding
  const scaleRef = useRef(scale);
  const positionRef = useRef(position);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const mediaContainerRef = useRef(null);
  const mediaRef = useRef(null);
  const pinchStartDistRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, initialIndex]);

  // Constrain limits reactively if zoom level changes (keeps bounds sticky if zoomed out slowly)
  useEffect(() => {
    setPosition((prev) =>
      calculateBoundedOffset(
        prev.x,
        prev.y,
        scale,
        mediaRef.current,
        mediaContainerRef.current,
      ),
    );
  }, [scale]);

  // Reset position automatically if scale drops back to default
  useEffect(() => {
    if (scale <= ZOOM_MIN) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  const handleNext = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (mediaList.length <= 1) return;
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setCurrentIndex((prev) => (prev + 1) % mediaList.length);
    },
    [mediaList.length],
  );

  const handlePrev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (mediaList.length <= 1) return;
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setCurrentIndex(
        (prev) => (prev - 1 + mediaList.length) % mediaList.length,
      );
    },
    [mediaList.length],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && mediaList.length > 1) handleNext();
      if (e.key === "ArrowLeft" && mediaList.length > 1) handlePrev();
    },
    [isOpen, onClose, handleNext, handlePrev, mediaList.length],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Handle native events for zooming, panning, and swiping
  useEffect(() => {
    const container = mediaContainerRef.current;
    if (!container || !isOpen) return;

    let isDragging = false;
    let lastMouse = null;
    let lastTouch = null;

    // Touch Swipe tracking variables
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    // Trackpad swipe tracking variables
    let wheelAccumulatorX = 0;
    let lastWheelTime = 0;
    let wheelSwipeCooldown = false;
    let wheelTimeout = null;

    const handleWheel = (e) => {
      e.preventDefault(); // Prevent page scroll / browser swipe navigation

      if (e.ctrlKey) {
        // Trackpad pinch (zoom)
        const zoomSensitivity = 0.01;
        const delta = -e.deltaY * zoomSensitivity;
        setScale((prev) =>
          Math.min(Math.max(prev + delta, ZOOM_MIN), ZOOM_MAX),
        );
      } else {
        // Trackpad scroll (pan or swipe)
        if (scaleRef.current > 1) {
          // Panning when zoomed in (bounded)
          setPosition((prev) =>
            calculateBoundedOffset(
              prev.x - e.deltaX,
              prev.y - e.deltaY,
              scaleRef.current,
              mediaRef.current,
              container,
            ),
          );
        } else {
          // Swiping when at 1x scale (base size)
          const now = Date.now();

          // Reset accumulator if user stopped scrolling for a moment
          if (now - lastWheelTime > 150) {
            wheelAccumulatorX = 0;
          }
          lastWheelTime = now;

          if (!wheelSwipeCooldown) {
            // Only capture primarily horizontal wheel movements
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
              wheelAccumulatorX += e.deltaX;
              const swipeThreshold = 100; // Pixels of trackpad distance to trigger swipe

              if (wheelAccumulatorX > swipeThreshold) {
                // Swiped Left -> Next Image
                handleNext();
                wheelSwipeCooldown = true;
                wheelAccumulatorX = 0;
                wheelTimeout = setTimeout(() => {
                  wheelSwipeCooldown = false;
                }, 500); // 500ms cooldown before next swipe
              } else if (wheelAccumulatorX < -swipeThreshold) {
                // Swiped Right -> Prev Image
                handlePrev();
                wheelSwipeCooldown = true;
                wheelAccumulatorX = 0;
                wheelTimeout = setTimeout(() => {
                  wheelSwipeCooldown = false;
                }, 500);
              }
            }
          }
        }
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchStartDistRef.current = getDistance(e.touches);
      } else if (e.touches.length === 1) {
        // Record starting positions for swiping and panning
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;

        if (scaleRef.current > 1) {
          // 1 finger touch to pan
          lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStartDistRef.current) {
        // Pinch zooming
        e.preventDefault();
        const currentDist = getDistance(e.touches);
        const deltaDist = currentDist - pinchStartDistRef.current;
        const pinchSensitivity = 0.01;

        setScale((prev) =>
          Math.min(
            Math.max(prev + deltaDist * pinchSensitivity, ZOOM_MIN),
            ZOOM_MAX,
          ),
        );
        pinchStartDistRef.current = currentDist;
      } else if (e.touches.length === 1) {
        // Update end positions during swipe
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;

        if (scaleRef.current > 1 && lastTouch) {
          // 1 finger touch panning (bounded)
          e.preventDefault(); // Prevent swipe-to-navigate browser features
          const deltaX = e.touches[0].clientX - lastTouch.x;
          const deltaY = e.touches[0].clientY - lastTouch.y;

          setPosition((prev) =>
            calculateBoundedOffset(
              prev.x + deltaX,
              prev.y + deltaY,
              scaleRef.current,
              mediaRef.current,
              container,
            ),
          );
          lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else if (scaleRef.current <= 1) {
          // If at base scale, prevent browser's native horizontal swipe gesture (like going back a page)
          const dx = Math.abs(touchStartX - touchEndX);
          const dy = Math.abs(touchStartY - touchEndY);
          if (dx > dy && dx > 10) {
            e.preventDefault();
          }
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStartDistRef.current = null;
      if (e.touches.length === 0) {
        lastTouch = null;

        // Handle Touch Swipe Navigation when not zoomed in
        if (scaleRef.current <= 1) {
          const deltaX = touchStartX - touchEndX;
          const deltaY = touchStartY - touchEndY;
          const swipeThreshold = 50; // Minimum distance to register as a swipe

          // Verify swipe was primarily horizontal and exceeded the threshold
          if (
            Math.abs(deltaX) > swipeThreshold &&
            Math.abs(deltaX) > Math.abs(deltaY)
          ) {
            if (deltaX > 0) {
              // Swipe Left -> Show Next
              handleNext();
            } else {
              // Swipe Right -> Show Prev
              handlePrev();
            }
          }
        }

        // Reset touch coordinates
        touchStartX = 0;
        touchEndX = 0;
        touchStartY = 0;
        touchEndY = 0;
      }
    };

    // --- MOUSE DRAG EVENT HANDLERS ---
    const handleMouseDown = (e) => {
      if (scaleRef.current <= 1 || e.button !== 0) return;
      e.preventDefault(); // Stops browser's native image dragging
      isDragging = true;
      lastMouse = { x: e.clientX, y: e.clientY };
      container.style.cursor = "grabbing";
    };

    const handleMouseMove = (e) => {
      if (!isDragging || scaleRef.current <= 1) return;
      e.preventDefault();
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;

      setPosition((prev) =>
        calculateBoundedOffset(
          prev.x + dx,
          prev.y + dy,
          scaleRef.current,
          mediaRef.current,
          container,
        ),
      );
      lastMouse = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        lastMouse = null;
        container.style.cursor = scaleRef.current > 1 ? "grab" : "auto";
      }
    };

    // Attach native non-passive listeners
    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchcancel", handleTouchEnd);
    container.addEventListener("mousedown", handleMouseDown);

    // Bind mousemove/up to window so dragging doesn't break if mouse leaves container
    window.addEventListener("mousemove", handleMouseMove, { passive: false });
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      if (wheelTimeout) clearTimeout(wheelTimeout);
    };
  }, [isOpen, handleNext, handlePrev]);

  if (!isOpen || mediaList.length === 0) return null;

  const currentMedia = mediaList[currentIndex];
  const isMulti = mediaList.length > 1;

  const handleZoomIn = (e) => {
    e.stopPropagation();
    setScale((prev) => Math.min(prev + 0.5, ZOOM_MAX));
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    setScale((prev) => Math.max(prev - 0.5, ZOOM_MIN));
  };

  const handleResetZoom = (e) => {
    e.stopPropagation();
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const buttonStyle = {
    background: "rgba(30, 30, 30, 0.7)",
    border: "1px solid rgba(255,255,255,0.2)",
    color: "white",
    cursor: "pointer",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    transition: "background 0.2s",
  };

  // Helper styles applied directly to img/video tags
  const mediaElementStyle = {
    maxWidth: "100vw",
    maxHeight: "100svh",
    // Apply position transform BEFORE scale for expected 1:1 mouse tracking
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transition: scale === 1 ? "transform 0.2s ease-out" : "none",
    objectFit: "contain",
    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
    cursor: scale > 1 ? "grab" : "auto",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100svh",
        backgroundColor: "#000000",
        zIndex: 99999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        pointerEvents: "auto",
        overflow: "hidden",
      }}
    >
      {/* Top Bar: Indicator & Close */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          right: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <div
          style={{
            color: "white",
            fontSize: "14px",
            fontFamily: "sans-serif",
            background: "rgba(0,0,0,0.5)",
            padding: "6px 12px",
            borderRadius: "15px",
            opacity: isMulti ? 1 : 0,
          }}
        >
          {currentIndex + 1} / {mediaList.length}
        </div>
        <button
          onClick={onClose}
          style={{ ...buttonStyle, fontSize: "24px", lineHeight: "1" }}
          title="Close"
        >
          &times;
        </button>
      </div>

      {/* Navigation Prev */}
      {isMulti && (
        <button
          onClick={handlePrev}
          style={{
            ...buttonStyle,
            position: "absolute",
            left: "20px",
            zIndex: 10,
          }}
          title="Previous"
        >
          &#10094;
        </button>
      )}

      {/* Media Container */}
      <div
        ref={mediaContainerRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          touchAction: "none",
        }}
      >
        {isVideo(currentMedia) ? (
          <video
            ref={mediaRef}
            src={currentMedia}
            controls={scale <= ZOOM_MIN} // Hide controls while zooming to prevent UI overlap
            autoPlay
            playsInline
            draggable={false}
            style={mediaElementStyle}
          />
        ) : (
          <img
            ref={mediaRef}
            src={currentMedia}
            alt={`Preview ${currentIndex + 1}`}
            draggable={false} // Required: overrides standard image drag behavior
            style={mediaElementStyle}
          />
        )}
      </div>

      {/* Navigation Next */}
      {isMulti && (
        <button
          onClick={handleNext}
          style={{
            ...buttonStyle,
            position: "absolute",
            right: "20px",
            zIndex: 10,
          }}
          title="Next"
        >
          &#10095;
        </button>
      )}

      {/* Zoom Controls */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          bottom: "30px",
          display: "flex",
          gap: "10px",
          background: "rgba(20, 20, 20, 0.7)",
          padding: "8px 16px",
          borderRadius: "30px",
          border: "1px solid rgba(255,255,255,0.1)",
          zIndex: 10,
        }}
      >
        <button
          onClick={handleZoomOut}
          disabled={scale <= ZOOM_MIN}
          style={{
            ...buttonStyle,
            width: "36px",
            height: "36px",
            opacity: scale <= ZOOM_MIN ? 0.4 : 1,
          }}
        >
          -
        </button>
        <button
          onClick={handleResetZoom}
          style={{
            background: "transparent",
            border: "none",
            color: "white",
            cursor: "pointer",
            fontSize: "14px",
            padding: "0 10px",
            fontFamily: "sans-serif",
          }}
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          onClick={handleZoomIn}
          disabled={scale >= ZOOM_MAX}
          style={{
            ...buttonStyle,
            width: "36px",
            height: "36px",
            opacity: scale >= ZOOM_MAX ? 0.4 : 1,
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default IMAGE_FULL;
